/**
 * Mock CEO Command Center snapshot — aggregates derived + synthesized
 * telemetry from the Vault + marketplace + attestations. In the real
 * stack this is a server endpoint reading from the engine's activity
 * log and the attestor's witness state.
 */

import type {
  AlertSeverity,
  CommandAlert,
  CommandSnapshot,
  EntityId,
  TrustedTimestamp,
} from '../types';

import { snapshot as vaultSnapshot } from './store';

const MOCK_LATENCY_MS = 150;
const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export async function getCommandSnapshot(): Promise<CommandSnapshot> {
  await sleep(MOCK_LATENCY_MS);
  const vs = vaultSnapshot();
  const active_gis = vs.gis.length;

  // Seed a deterministic-but-lively set of numbers from the aggregate
  // activity the Vault already captures.
  const todayMinutes = minutesSinceUtcMidnight();
  const runs_today = Math.max(
    0,
    Math.round(active_gis * 6 + todayMinutes * 0.12 + noise(vs.activity.length, 11)),
  );
  const revenue_today_ils = Math.max(
    0,
    Math.round(active_gis * 4 + todayMinutes * 0.05 + noise(active_gis, 7)),
  );
  const attestations_today = Math.max(0, Math.round(runs_today * 0.92));

  // 14 daily samples for sparklines, oldest → newest. Semi-random-but-
  // stable so the chart doesn't dance on every re-render.
  const trends = {
    runs: daily(runs_today, 0.62, 1.18, 14, 3),
    revenue_ils: daily(revenue_today_ils, 0.55, 1.25, 14, 5),
    attestations: daily(attestations_today, 0.65, 1.15, 14, 7),
  };

  // Alerts: build from the Vault's recent failures / revenue events,
  // plus one synthetic witness-down line so the Alerts section is
  // always expressive in the demo.
  const alerts: CommandAlert[] = [];
  const now: TrustedTimestamp = {
    iso: new Date().toISOString(),
    source: 'Monotonic',
  };
  for (const ev of vs.activity) {
    if (ev.kind === 'forked') {
      alerts.push({
        kind: 'new_fork',
        severity: 'info',
        root_id: ev.root_id,
        name: ev.name,
        at: ev.at,
        by: ev.by,
      });
    } else if (ev.kind === 'revenue_in' && ev.amount_ils >= 10) {
      alerts.push({
        kind: 'revenue_spike',
        severity: 'info',
        root_id: ev.root_id,
        name: ev.name,
        at: ev.at,
        amount_ils: ev.amount_ils,
      });
    } else if (ev.kind === 'verified' && !ev.ok) {
      alerts.push({
        kind: 'gi_failed',
        severity: 'warn',
        root_id: ev.root_id,
        name: ev.name,
        at: ev.at,
        reason: 'Verification failed on last run',
      });
    }
  }
  // Add one critical + one warn synthetic alert so the layout always
  // shows both tiers. Tie them to real GIs so the click-through
  // lands somewhere sensible.
  const firstGi = vs.gis[0];
  if (firstGi) {
    alerts.unshift({
      kind: 'gi_failed',
      severity: 'critical' satisfies AlertSeverity,
      root_id: firstGi.root_id,
      name: firstGi.name,
      at: now,
      reason: 'Fuel exhausted on last execution (demo)',
    });
  }
  alerts.unshift({
    kind: 'witness_down',
    severity: 'warn',
    at: now,
    witness: 'rfc3161.demo.local',
  });

  return {
    stats: {
      runs_today,
      revenue_today_ils,
      active_gis,
      attestations_today,
    },
    trends,
    alerts: alerts.slice(0, 10),
    activity: vs.activity.slice(0, 16),
  };
}

// ─── Helpers ───────────────────────────────────────────────────────

function minutesSinceUtcMidnight(): number {
  const d = new Date();
  return d.getUTCHours() * 60 + d.getUTCMinutes();
}
function noise(seed: number, prime: number): number {
  const v = Math.sin(seed * prime) * 10_000;
  return v - Math.floor(v);
}
function daily(today: number, min: number, max: number, days: number, seed: number): number[] {
  const out: number[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const n = noise(i + seed, 13);
    const factor = min + (max - min) * n;
    out.push(Math.max(0, Math.round(today * factor)));
  }
  return out;
}

// Keep `EntityId` import pinned — CommandAlert uses it via the
// discriminated-union shape.
void (null as EntityId | null);
